// src/components/timer/FloatingTimer.tsx
// Timer flutuante draggable — mouse (desktop) + touch (tablet/mobile)

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Maximize2, X, GripVertical } from 'lucide-react';
import { useStudyTimer } from '../../contexts/StudyTimerContext';
import { getTypeByValue } from '../planner/constants/studyTypes';
import './FloatingTimer.css';

const LS_POS_KEY = 'graia_floating_timer_pos';

const fmt = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const getSavedPos = () => {
    try {
        const raw = localStorage.getItem(LS_POS_KEY);
        if (raw) return JSON.parse(raw);
    } catch { }
    return { x: 20, y: 80 };
};

const FloatingTimer: React.FC = () => {
    const { activeTimer, isFullScreen, openFullScreen, openStopModal } = useStudyTimer();
    const location = useLocation();

    const isOnPlanner = location.pathname.includes('/planner') ||
        location.pathname.includes('/dashboard');

    const shouldShow = !!activeTimer && !isOnPlanner && !isFullScreen;

    const [pos, setPos] = useState<{ x: number; y: number }>(getSavedPos);
    const dragging = useRef(false);
    const offset = useRef({ x: 0, y: 0 });
    const ref = useRef<HTMLDivElement>(null);

    const savePos = useCallback((newPos: { x: number; y: number }) => {
        localStorage.setItem(LS_POS_KEY, JSON.stringify(newPos));
    }, []);

    const clampPos = useCallback((x: number, y: number) => ({
        x: Math.max(0, Math.min(window.innerWidth - 200, x)),
        y: Math.max(0, Math.min(window.innerHeight - 80, y)),
    }), []);

    // ── MOUSE events ─────────────────────────────────────────────────────
    const onMouseDown = useCallback((e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button')) return;
        dragging.current = true;
        offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    }, [pos]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!dragging.current) return;
            const newPos = clampPos(e.clientX - offset.current.x, e.clientY - offset.current.y);
            setPos(newPos);
        };
        const onMouseUp = (e: MouseEvent) => {
            if (!dragging.current) return;
            dragging.current = false;
            const newPos = clampPos(e.clientX - offset.current.x, e.clientY - offset.current.y);
            setPos(newPos);
            savePos(newPos);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [savePos, clampPos]);

    // ── TOUCH events (tablet/mobile) ─────────────────────────────────────
    const onTouchStart = useCallback((e: React.TouchEvent) => {
        if ((e.target as HTMLElement).closest('button')) return;
        const touch = e.touches[0];
        dragging.current = true;
        offset.current = { x: touch.clientX - pos.x, y: touch.clientY - pos.y };
    }, [pos]);

    useEffect(() => {
        const onTouchMove = (e: TouchEvent) => {
            if (!dragging.current) return;
            e.preventDefault();
            const touch = e.touches[0];
            const newPos = clampPos(touch.clientX - offset.current.x, touch.clientY - offset.current.y);
            setPos(newPos);
        };
        const onTouchEnd = (e: TouchEvent) => {
            if (!dragging.current) return;
            dragging.current = false;
            const touch = e.changedTouches[0];
            const newPos = clampPos(touch.clientX - offset.current.x, touch.clientY - offset.current.y);
            setPos(newPos);
            savePos(newPos);
        };
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
        return () => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [savePos, clampPos]);

    if (!shouldShow) return null;

    const typeInfo = getTypeByValue(activeTimer!.type);
    const typeLabel = activeTimer!.type === 'other'
        ? (activeTimer!.customType || 'Outro')
        : typeInfo.label;
    const accentColor = activeTimer!.type === 'other'
        ? (activeTimer!.color || '#6B7280')
        : typeInfo.color;

    return (
        <div
            ref={ref}
            className="flt-timer"
            style={{ left: pos.x, top: pos.y }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
        >
            <div className="flt-accent" style={{ backgroundColor: accentColor }}>
                <GripVertical size={14} className="flt-grip-icon" />
            </div>

            <button className="flt-body" onClick={openFullScreen} title="Abrir em tela cheia">
                <span className="flt-type">{typeLabel}</span>
                <span className="flt-time">{fmt(activeTimer!.elapsed)}</span>
            </button>

            <div className="flt-actions">
                <button className="flt-btn" onClick={openFullScreen} title="Tela cheia">
                    <Maximize2 size={13} />
                </button>
                <button className="flt-btn flt-btn-close" onClick={openStopModal} title="Encerrar">
                    <X size={13} />
                </button>
            </div>
        </div>
    );
};

export default FloatingTimer;